import { Layer } from 'mapbox-gl';

import { SymbolLayerWithMapIcon } from '../components/MapIcon';
import {
  ARTIFICIAL_REEFS_ICON_SIZE,
  SELECTED_ARTIFICIAL_REEFS_ICON_SIZE,
} from '../constants/mapLayers/artificialReefs';
import {
  CATCH_POSITIONS_ICON_SIZE,
  SELECTED_CATCH_POSITION_ICON_SIZE,
} from '../constants/mapLayers/catchPositions';
import {
  EMPTY_FISHING_WATERS_ICON_SIZE,
  FISHING_WATERS_ICON_SIZE,
  SELECTED_EMPTY_FISHING_WATER_ICON_SIZE,
  SELECTED_FISHING_WATER_ICON_SIZE,
} from '../constants/mapLayers/fishingWaters';
import {
  POINTS_OF_INTEREST_ICON_SIZE,
  SELECTED_POINT_OF_INTEREST_ICON_SIZE,
} from '../constants/mapLayers/pointsOfInterest';
import { SELECTED_WAYPOINT_ICON_SIZE, WAYPOINTS_ICON_SIZE } from '../constants/mapLayers/waypoints';
import { DrawerView, FishbrainLayers, LayerWithToggleableGroup } from '../types/types';

import {
  ARTIFICIAL_REEFS_HIGHLIGHT_LAYER,
  ARTIFICIAL_REEFS_LAYER,
  ARTIFICIAL_REEFS_SIMPLIFIED_HIGHLIGHT_LAYER,
} from './mapLayers/artificialReefs';
import {
  CATCH_POSITIONS_HIGHLIGHT_LAYER,
  CATCH_POSITIONS_LAYER,
  CATCH_POSITIONS_SIMPLIFIED_HIGHLIGHT_LAYER,
} from './mapLayers/catchPositions';
import {
  EMPTY_FISHING_WATERS_LAYER,
  FISHING_WATERS_HIGHLIGHT_LAYER,
  FISHING_WATERS_LAYER,
} from './mapLayers/fishingWaters';
import {
  POINTS_OF_INTEREST_HIGHLIGHT_LAYER,
  POINTS_OF_INTEREST_LAYERS,
} from './mapLayers/pointsOfInterest';
import {
  WAYPOINTS_HIGHLIGHT_LAYER,
  WAYPOINTS_LAYERS,
  WAYPOINTS_SIMPLIFIED_HIGHLIGHT_LAYER,
} from './mapLayers/waypoints';

interface HighlightLayer {
  filterProperty: string;
  highlightLayers: (LayerWithToggleableGroup & (Layer | SymbolLayerWithMapIcon))[];
  sourceLayers: (LayerWithToggleableGroup & SymbolLayerWithMapIcon)[];
  getIconSizes: (sourceLayerId: string) => { normalIconSize: number; enlargedIconSize: number };
  drawerView: DrawerView;
}

const getFishingWaterIconSizes: HighlightLayer['getIconSizes'] = sourceLayerId => {
  if (sourceLayerId === EMPTY_FISHING_WATERS_LAYER.id) {
    return {
      normalIconSize: EMPTY_FISHING_WATERS_ICON_SIZE,
      enlargedIconSize: SELECTED_EMPTY_FISHING_WATER_ICON_SIZE,
    };
  }

  return {
    normalIconSize: FISHING_WATERS_ICON_SIZE,
    enlargedIconSize: SELECTED_FISHING_WATER_ICON_SIZE,
  };
};

const getCatchPositionsIconSizes: HighlightLayer['getIconSizes'] = () => ({
  normalIconSize: CATCH_POSITIONS_ICON_SIZE,
  enlargedIconSize: SELECTED_CATCH_POSITION_ICON_SIZE,
});

const getPointsOfInterestIconSizes: HighlightLayer['getIconSizes'] = () => ({
  normalIconSize: POINTS_OF_INTEREST_ICON_SIZE,
  enlargedIconSize: SELECTED_POINT_OF_INTEREST_ICON_SIZE,
});

const getWaypointsIconSizes: HighlightLayer['getIconSizes'] = () => ({
  normalIconSize: WAYPOINTS_ICON_SIZE,
  enlargedIconSize: SELECTED_WAYPOINT_ICON_SIZE,
});

const getArtificialReefsIconSizes: HighlightLayer['getIconSizes'] = () => ({
  normalIconSize: ARTIFICIAL_REEFS_ICON_SIZE,
  enlargedIconSize: SELECTED_ARTIFICIAL_REEFS_ICON_SIZE,
});

export const HIGHLIGHT_LAYERS: Omit<FishbrainLayers<HighlightLayer>, 'spotPrediction'> = {
  fishingWaters: {
    highlightLayers: [FISHING_WATERS_HIGHLIGHT_LAYER],
    sourceLayers: [FISHING_WATERS_LAYER, EMPTY_FISHING_WATERS_LAYER],
    filterProperty: 'external_id',
    getIconSizes: getFishingWaterIconSizes,
    drawerView: 'fishing-water',
  },
  catchPositions: {
    highlightLayers: [CATCH_POSITIONS_HIGHLIGHT_LAYER, CATCH_POSITIONS_SIMPLIFIED_HIGHLIGHT_LAYER],
    sourceLayers: [CATCH_POSITIONS_LAYER],
    filterProperty: 'external_id',
    getIconSizes: getCatchPositionsIconSizes,
    drawerView: 'catch',
  },
  pointsOfInterest: {
    highlightLayers: [POINTS_OF_INTEREST_HIGHLIGHT_LAYER],
    sourceLayers: Object.values(POINTS_OF_INTEREST_LAYERS),
    filterProperty: 'id',
    getIconSizes: getPointsOfInterestIconSizes,
    drawerView: 'poi',
  },
  waypoints: {
    highlightLayers: [WAYPOINTS_HIGHLIGHT_LAYER, WAYPOINTS_SIMPLIFIED_HIGHLIGHT_LAYER],
    sourceLayers: Object.values(WAYPOINTS_LAYERS),
    filterProperty: 'external_id',
    getIconSizes: getWaypointsIconSizes,
    drawerView: 'waypoint',
  },
  artificialReefs: {
    highlightLayers: [
      ARTIFICIAL_REEFS_HIGHLIGHT_LAYER,
      ARTIFICIAL_REEFS_SIMPLIFIED_HIGHLIGHT_LAYER,
    ],
    sourceLayers: [ARTIFICIAL_REEFS_LAYER],
    filterProperty: 'id',
    getIconSizes: getArtificialReefsIconSizes,
    drawerView: 'artificial-reef',
  },
};
